import React from 'react';
import { User, Calendar, CheckCircle } from 'lucide-react';
import clsx from 'clsx';
import Tooltip from './Tooltip';
import { LoadingState } from '@components/common/loading';
import styles from './AssessorSelector.module.css';

export interface AssessorOption {
  assessorId: number;
  assessorName: string;
  email: string;
  totalMeetings?: number;
  scheduledMeetings?: number;
  completedMeetings?: number;
}

interface AssessorSelectorProps {
  assessors: AssessorOption[];
  selectedAssessorId: number | null;
  onSelectAssessor: (id: number) => void;
  isLoading?: boolean;
  disabled?: boolean;
  showWorkload?: boolean;
  layout?: 'list' | 'grid';
  emptyMessage?: string;
  className?: string;
}

const AssessorSelector: React.FC<AssessorSelectorProps> = ({
  assessors,
  selectedAssessorId,
  onSelectAssessor,
  isLoading = false,
  disabled = false,
  showWorkload = false,
  layout = 'list',
  emptyMessage = 'No assessors found',
  className = ''
}) => {
  if (isLoading) {
    return <LoadingState message="Loading assessors..." />;
  }

  if (!assessors || assessors.length === 0) {
    return (
      <div className="empty-state">
        <User size={48} className="icon-muted" />
        <p>{emptyMessage}</p>
      </div>
    );
  }

  const containerClass = clsx(styles.assessorsList, layout === 'grid' && styles.assessorsGrid, className);

  return (
    <div className={containerClass}>
      {assessors.map((assessor) => (
        <label
          key={assessor.assessorId}
          className={clsx(styles.assessorOption, selectedAssessorId === assessor.assessorId && styles.selected)}
        >
          <input
            type="radio"
            name="assessor"
            value={assessor.assessorId}
            checked={selectedAssessorId === assessor.assessorId}
            onChange={() => onSelectAssessor(assessor.assessorId)}
            disabled={disabled}
          />
          <div className={styles.assessorIcon}>
            <User size={16} />
          </div>
          <div className={styles.assessorInfo}>
            <div className={styles.assessorNameSection}>
              <span className={styles.assessorName}>{assessor.assessorName}</span>
              <span className={styles.assessorEmail}>{assessor.email}</span>
            </div>
          </div>
          {showWorkload && (
            <div className={styles.assessorWorkload}>
              <Tooltip content="Total assigned meetings" position="top">
                <div className={styles.workloadItem}>
                  <User size={12} />
                  <span>{assessor.totalMeetings || 0}</span>
                </div>
              </Tooltip>
              <Tooltip content="Scheduled meetings" position="top">
                <div className={styles.workloadItem}>
                  <Calendar size={12} />
                  <span>{assessor.scheduledMeetings || 0}</span>
                </div>
              </Tooltip>
              <Tooltip content="Completed meetings" position="top">
                <div className={styles.workloadItem}>
                  <CheckCircle size={12} />
                  <span>{assessor.completedMeetings || 0}</span>
                </div>
              </Tooltip>
            </div>
          )}
          <div className={styles.radioIndicator}></div>
        </label>
      ))}
    </div>
  );
};

export default AssessorSelector;
